<template>
  <div class="inline-auth-container" v-if="show">
    <transition name="slide-fade">
      <div class="auth-fields-wrapper" v-if="isVisible && !showPasswordReset">
        <!-- Existing User Login -->
        <div v-if="userExists" class="login-section">
          <div class="section-header mb-4">
            <h3 class="text-lg font-semibold text-gray-900">Welcome back!</h3>
            <p class="text-sm text-gray-600">Enter your password to continue</p>
          </div>
          
          <form @submit.prevent="handleLogin" class="space-y-4">
            <div>
              <label for="inline-password" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
                Password
              </label>
              <div class="relative">
                <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
                  <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <input 
                  id="inline-password" 
                  v-model="loginForm.password" 
                  type="password" 
                  required 
                  class="form-input block w-full placeholder-gray-400 transition-all" 
                  style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
                  :style="{ borderColor: focusedField === 'password' ? '#EC4899' : '#D1D5DB' }"
                  @focus="focusedField = 'password'"
                  @blur="focusedField = ''"
                  placeholder="Enter your password"
                >
              </div>
            </div>
            
            <div class="flex items-center justify-between">
              <button 
                type="button"
                @click="showForgotPassword"
                :disabled="isLoading"
                class="text-sm text-pink-600 hover:text-pink-700 font-medium disabled:opacity-50"
              >
                <span v-if="!isLoading">Forgot your password?</span>
                <span v-else>Sending code...</span>
              </button>
              
              <button 
                type="submit" 
                :disabled="isLoading"
                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50"
              >
                <span v-if="!isLoading">Sign In & Continue</span>
                <span v-else class="flex items-center">
                  <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              </button>
            </div>
          </form>
        </div>
        
        <!-- New User Registration -->
        <div v-else class="registration-section">
          <div class="section-header mb-4">
            <h3 class="text-lg font-semibold text-gray-900">Create your account</h3>
            <p class="text-sm text-gray-600">Complete your registration to continue</p>
          </div>
          
          <form @submit.prevent="handleRegister" class="space-y-4">
            <!-- Hidden honeypot field -->
            <div class="hidden">
              <input v-model="registerForm.fax" type="text" autocomplete="off">
            </div>

            <!-- Cloudflare Turnstile -->
            <div ref="turnstileContainer" class="mt-2"></div>

            <!-- Name and Phone on same row (50/50) -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label for="inline-name" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
                  Full Name
                </label>
                <div class="relative">
                  <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
                    <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
                  <input 
                    id="inline-name" 
                    v-model="registerForm.name" 
                    type="text" 
                    autocomplete="name" 
                    required 
                    class="form-input block w-full placeholder-gray-400 transition-all"
                    style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
                    :style="{ borderColor: focusedField === 'name' ? '#EC4899' : '#D1D5DB' }"
                    @focus="focusedField = 'name'"
                    @blur="focusedField = ''"
                    placeholder="Enter your full name"
                  >
                </div>
              </div>
              
              <div>
                <label for="inline-phone" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
                  Phone
                </label>
                <div class="relative">
                  <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
                    <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                  </div>
                  <input 
                    id="inline-phone" 
                    v-model="registerForm.phone" 
                    type="tel" 
                    autocomplete="tel" 
                    required 
                    maxlength="16"
                    @keydown="enforcePhoneFormat"
                    @keyup="formatToPhone"
                    class="form-input block w-full placeholder-gray-400 transition-all"
                    style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
                    :style="{ borderColor: focusedField === 'phone' ? '#EC4899' : '#D1D5DB' }"
                    @focus="focusedField = 'phone'"
                    @blur="focusedField = ''"
                    placeholder="(555) 123-4567"
                  >
                </div>
              </div>
            </div>
            
            <!-- Address on its own row -->
            <div>
              <label for="inline-address" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
                Address
              </label>
              <div class="relative">
                <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
                  <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                </div>
                <input 
                  id="inline-address-autocomplete" 
                  v-model="registerForm.fullAddress" 
                  type="text" 
                  autocomplete="off" 
                  required 
                  class="form-input block w-full placeholder-gray-400 transition-all"
                  style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
                  :style="{ borderColor: focusedField === 'address' ? '#EC4899' : '#D1D5DB' }"
                  @focus="focusedField = 'address'"
                  @blur="focusedField = ''"
                  placeholder="Start typing your address..."
                >
                <!-- Hidden fields for parsed address components -->
                <input type="hidden" v-model="registerForm.address" />
                <input type="hidden" v-model="registerForm.city" />
                <input type="hidden" v-model="registerForm.state" />
              </div>
            </div>
            
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label for="inline-password" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
                  Password
                </label>
                <div class="relative">
                  <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
                    <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                  </div>
                  <input 
                    id="inline-password-new" 
                    v-model="registerForm.password" 
                    type="password" 
                    autocomplete="new-password" 
                    required 
                    class="form-input block w-full placeholder-gray-400 transition-all"
                    style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
                    :style="{ borderColor: focusedField === 'password-new' ? '#EC4899' : '#D1D5DB' }"
                    @focus="focusedField = 'password-new'"
                    @blur="focusedField = ''"
                    placeholder="Create a password"
                  >
                </div>
              </div>
              
              <div>
                <label for="inline-password-confirm" class="block font-semibold mb-2" style="font-size: 0.875rem; color: #374151;">
                  Confirm Password
                </label>
                <div class="relative">
                  <div class="absolute flex items-center pointer-events-none" style="left: 0; top: 0; bottom: 0; padding-left: 0.75rem;">
                    <svg class="text-gray-400" style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                  </div>
                  <input 
                    id="inline-password-confirm" 
                    v-model="registerForm.passwordConfirmation" 
                    type="password" 
                    autocomplete="new-password" 
                    required 
                    class="form-input block w-full placeholder-gray-400 transition-all"
                    style="padding: 0.75rem 0.75rem 0.75rem 2.5rem; border: 2px solid #D1D5DB; border-radius: 0.5rem; font-size: 1rem;"
                    :style="{ borderColor: focusedField === 'password-confirm' ? '#EC4899' : '#D1D5DB' }"
                    @focus="focusedField = 'password-confirm'"
                    @blur="focusedField = ''"
                    placeholder="Confirm your password"
                  >
                </div>
              </div>
            </div>
            
            <div class="flex justify-end">
              <button 
                type="submit" 
                :disabled="isLoading"
                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50"
              >
                <span v-if="!isLoading">Create Account & Continue</span>
                <span v-else class="flex items-center">
                  <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating account...
                </span>
              </button>
            </div>
          </form>
        </div>
        
        <!-- Error Message with auto-hide -->
        <transition name="fade">
          <div v-if="errorMessage" class="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {{ errorMessage }}
          </div>
        </transition>
        
        <!-- Success Message -->
        <div v-if="successMessage" class="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {{ successMessage }}
        </div>
      </div>
    </transition>
    
    <!-- Password Reset Component -->
    <transition name="slide-fade">
      <inline-password-reset 
        v-if="showPasswordReset"
        :user-email="userEmail"
        @auth-success="handleAuthSuccess"
        @cancel="cancelPasswordReset"
      />
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import InlinePasswordReset from './InlinePasswordReset.vue';

export default {
  name: 'InlineAuth',
  
  components: {
    InlinePasswordReset
  },
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    userExists: {
      type: Boolean,
      default: false
    },
    userEmail: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      isVisible: false,
      isLoading: false,
      focusedField: '',
      errorMessage: '',
      successMessage: '',
      showPasswordReset: false,
      
      loginForm: {
        email: '',
        password: '',
        remember: true
      },
      
      registerForm: {
        name: '',
        email: '',
        password: '',
        passwordConfirmation: '',
        fullAddress: '', // Display field for autocomplete
        address: '', // Hidden field for street address
        city: '', // Hidden field for city
        state: '', // Hidden field for state
        zip: '', // Hidden field for postal code
        phone: '',
        fax: '', // Honeypot field
        formTime: Date.now()
      },

      turnstileToken: null,
      turnstileWidgetId: null,

      states: [
        { value: 'AL', label: 'Alabama' },
        { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' },
        { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' },
        { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' },
        { value: 'DE', label: 'Delaware' },
        { value: 'DC', label: 'District Of Columbia' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' },
        { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' },
        { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' },
        { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' },
        { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' },
        { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' },
        { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' },
        { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' },
        { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' },
        { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' },
        { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' },
        { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' },
        { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' },
        { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' },
        { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' },
        { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' },
        { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' },
        { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' },
        { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' },
        { value: 'WY', label: 'Wyoming' }
      ]
    };
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.isVisible = true;
          // The Turnstile container lives inside the (v-if) registration form, so it does
          // not exist when the component first mounts — renderTurnstile() ran on mount and
          // early-returned. Re-invoke it here, after the form becomes visible, otherwise the
          // widget never renders, no token is produced, and turnstile:strict blocks the signup.
          this.$nextTick(() => this.loadTurnstile());
        });
      } else {
        this.isVisible = false;
        // Clear error message when component is hidden
        this.errorMessage = '';
        this.successMessage = '';
        // The container is destroyed by v-if when hidden — drop the now-stale widget.
        this.resetTurnstile();
      }
    },

    // Existing email (login) and new email (registration) render different views, and the
    // Turnstile container lives ONLY in the registration view. When the user flips between
    // them while the panel is already open (e.g. edits an existing email to a new one),
    // `show` doesn't change — so the show() watcher won't fire. Re-sync the widget here.
    userExists(newVal) {
      if (!this.show) return;
      if (newVal) {
        this.resetTurnstile();                        // -> login view: drop the stale widget
      } else {
        this.$nextTick(() => this.loadTurnstile());   // -> registration view: render into the fresh container
      }
    },

    userEmail(newVal) {
      this.loginForm.email = newVal;
      this.registerForm.email = newVal;
      // Clear any errors when email changes
      this.errorMessage = '';
    },
    
    errorMessage(newVal) {
      // Auto-clear error message after 5 seconds
      if (newVal) {
        setTimeout(() => {
          this.errorMessage = '';
        }, 5000);
      }
    }
  },
  
  methods: {
    isModifierKey(event) {
      const key = event.keyCode;
      return (event.shiftKey === true || key === 35 || key === 36) || // Allow Shift, Home, End
             (key === 8 || key === 9 || key === 13 || key === 46) || // Allow Backspace, Tab, Enter, Delete
             (key > 36 && key < 41) || // Allow left, up, right, down
             (
               // Allow Ctrl/Command + A,C,V,X,Z
               (event.ctrlKey === true || event.metaKey === true) &&
               (key === 65 || key === 67 || key === 86 || key === 88 || key === 90)
             );
    },
    
    enforcePhoneFormat(event) {
      // Input must be of a valid number format or a modifier key
      if(!this.isModifierKey(event) && !this.isNumericInput(event)) {
        event.preventDefault();
      }
    },
    
    isNumericInput(event) {
      const key = event.keyCode;
      return ((key >= 48 && key <= 57) || // Allow number line
              (key >= 96 && key <= 105)); // Allow number pad
    },
    
    formatToPhone(event) {
      if(this.isModifierKey(event)) { return; }

      const input = event.target.value.replace(/\D/g,'').substring(0,10);
      const areaCode = input.substring(0,3);
      const middle = input.substring(3,6);
      const last = input.substring(6,10);

      if(input.length > 6) {
        event.target.value = `(${areaCode}) ${middle}-${last}`;
      } else if(input.length > 3) {
        event.target.value = `(${areaCode}) ${middle}`;
      } else if(input.length > 0) {
        event.target.value = `(${areaCode}`;
      }
      
      this.registerForm.phone = event.target.value;
    },
    
    async handleLogin() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const response = await axios.post('/login', {
          email: this.loginForm.email,
          password: this.loginForm.password,
          remember: this.loginForm.remember
        });
        
        if (response.data.success || response.status === 200) {
          this.successMessage = 'Successfully signed in! Continuing...';
          this.$emit('auth-success', response.data.user || {}, 'login');
          
          // Clear form
          this.loginForm.password = '';
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = 'Invalid email or password. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    async handleRegister() {
      this.isLoading = true;
      this.errorMessage = '';
      
      // Enforce address selection similar to event location step
      try {
        const addressInput = document.getElementById('inline-address-autocomplete');
        if (addressInput && addressInput.value && (!this.registerForm.city || !this.registerForm.state)) {
          const helpers = window.campaignAddressHelpers || {};
          const parsed = helpers.parseAddressString ? helpers.parseAddressString(addressInput.value) : {};

          const confirmAndFill = async (city, state, zip) => {
            if (!helpers.showLocationConfirmModal) return false;
            return new Promise((resolve) => {
              helpers.showLocationConfirmModal(
                city,
                state,
                () => {
                  this.registerForm.city = city;
                  this.registerForm.state = state;
                  if (zip) this.registerForm.zip = zip;
                  resolve(true);
                },
                () => {
                  addressInput.focus();
                  this.isLoading = false;
                  this.errorMessage = 'Please select your address from the dropdown suggestions for accurate location data.';
                  resolve(false);
                },
                addressInput.value
              );
            });
          };

          // 1) Try simple parse
          if (parsed.city && parsed.state) {
            const ok = await confirmAndFill(parsed.city, parsed.state, parsed.zip);
            if (!ok) return;
          } else if (helpers.geocodeAddress) {
            // 2) Fallback to geocoding
            const geo = await helpers.geocodeAddress(addressInput.value);
            if (geo.city && geo.state) {
              const ok = await confirmAndFill(geo.city, geo.state, geo.zip);
              if (!ok) return;
            } else {
              // 3) Hard stop; ask user to pick a suggestion
              addressInput.focus();
              this.isLoading = false;
              this.errorMessage = 'Please select your address from the suggestions. We need city and state for your account.';
              return;
            }
          } else {
            addressInput.focus();
            this.isLoading = false;
            this.errorMessage = 'Please select your address from the suggestions for accurate location data.';
            return;
          }
        }
      } catch (e) {
        // non-fatal
      }

      // Validate passwords match
      if (this.registerForm.password !== this.registerForm.passwordConfirmation) {
        this.errorMessage = 'Passwords do not match.';
        this.isLoading = false;
        return;
      }
      
      // Check honeypot
      if (this.registerForm.fax) {
        this.errorMessage = 'Registration failed. Please try again.';
        this.isLoading = false;
        return;
      }
      
      try {
        const response = await axios.post('/user-register', {
          name: this.registerForm.name,
          email: this.registerForm.email,
          password: this.registerForm.password,
          password_confirmation: this.registerForm.passwordConfirmation,
          address: this.registerForm.address,
          city: this.registerForm.city,
          state: this.registerForm.state,
          zip: this.registerForm.zip,
          phone: this.registerForm.phone,
          formTime: this.registerForm.formTime,
          'cf-turnstile-response': this.turnstileToken,
          'turnstile_available': this.turnstileToken ? '1' : '0',
        });
        
        if (response.data.success) {
          this.successMessage = 'Account created successfully! Continuing...';
          this.$emit('auth-success', response.data.user || {}, 'register');
          
          // Clear sensitive data
          this.registerForm.password = '';
          this.registerForm.passwordConfirmation = '';
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = 'Registration failed. Please try again.';
        }

        // Reset Turnstile for retry
        if (typeof window.turnstile !== 'undefined' && this.turnstileWidgetId !== null) {
          window.turnstile.reset(this.turnstileWidgetId);
          this.turnstileToken = null;
        }
      } finally {
        this.isLoading = false;
      }
    },

    async showForgotPassword() {
      // Clear any existing errors first
      this.errorMessage = '';
      
      // Check if email is provided
      if (!this.userEmail || !this.userEmail.trim()) {
        // Use parent's error display if available, otherwise show inline
        if (window.showErrorMessage) {
          window.showErrorMessage('Please enter your email address first.');
        } else {
          this.errorMessage = 'Please enter your email address first.';
        }
        return;
      }
      
      this.isLoading = true;
      
      try {
        const response = await axios.post('/password/code-ajax', {
          email: this.userEmail
        });
        
        if (response.data.success) {
          this.showPasswordReset = true;
          this.isVisible = false;
        }
      } catch (error) {
        const errorMsg = 'Failed to send reset code. Please try again.';
        // Use parent's error display if available
        if (window.showErrorMessage) {
          window.showErrorMessage(errorMsg);
        } else {
          this.errorMessage = errorMsg;
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    cancelPasswordReset() {
      this.showPasswordReset = false;
      this.isVisible = true;
    },
    
    handleAuthSuccess(user, action = 'login') {
      this.$emit('auth-success', user, action);
    },
    
    resetTurnstile() {
      // Remove the Cloudflare widget (frees its iframe/resources) and clear our token so a
      // stale token is never submitted and the container can re-render cleanly next time.
      if (typeof window.turnstile !== 'undefined' && this.turnstileWidgetId !== null) {
        try { window.turnstile.remove(this.turnstileWidgetId); } catch (e) { /* noop */ }
      }
      this.turnstileWidgetId = null;
      this.turnstileToken = null;
    },

    loadTurnstile() {
      if (typeof window.turnstile !== 'undefined') {
        this.renderTurnstile();
        return;
      }
      if (!document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoadInline';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        window.onTurnstileLoadInline = () => {
          this.renderTurnstile();
        };
      } else {
        // Script tag exists but may not have loaded yet — poll for it
        const checkInterval = setInterval(() => {
          if (typeof window.turnstile !== 'undefined') {
            clearInterval(checkInterval);
            this.renderTurnstile();
          }
        }, 100);
        setTimeout(() => clearInterval(checkInterval), 5000);
      }
    },

    renderTurnstile() {
      if (!this.$refs.turnstileContainer || typeof window.turnstile === 'undefined') return;
      // Idempotent: skip if a widget is already tracked OR already present in the container.
      // The container's only child would be a rendered Turnstile widget, so this also guards
      // against any double-invocation race from the show/userExists watchers (avoids stacked iframes).
      if (this.turnstileWidgetId !== null || this.$refs.turnstileContainer.childElementCount > 0) return;
      const siteKey = document.querySelector('meta[name="turnstile-site-key"]')?.getAttribute('content');
      if (!siteKey) return;
      this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
        sitekey: siteKey,
        appearance: 'interaction-only',
        theme: 'light',
        callback: (token) => { this.turnstileToken = token; },
        'error-callback': () => { this.turnstileToken = null; }
      });
    },

    initializeAddressAutocomplete() {
      // Check if Google Maps API is loaded
      if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
        // Try again in a moment
        setTimeout(() => this.initializeAddressAutocomplete(), 500);
        return;
      }
      
      const addressInput = document.getElementById('inline-address-autocomplete');
      if (!addressInput) {
        // Element not ready yet, try again
        this.$nextTick(() => {
          setTimeout(() => this.initializeAddressAutocomplete(), 100);
        });
        return;
      }
      
      // Initialize autocomplete
      const autocomplete = new google.maps.places.Autocomplete(addressInput, {
        types: ['address']
        // International allowed (no componentRestrictions)
      });
      
      // Listen for place selection
      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (!place.address_components) {
          return;
        }
        
        // Parse address components
        let streetNumber = '';
        let streetName = '';
        let city = '';
        let state = '';
        let zipCode = '';
        
        for (const component of place.address_components) {
          const types = component.types;
          
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          
          if (types.includes('route')) {
            streetName = component.long_name;
          }
          
          if (types.includes('locality')) {
            city = component.long_name;
          }
          
          if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          }
          
          if (types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        }
        
        // Update form fields
        const fullStreetAddress = streetNumber ? `${streetNumber} ${streetName}` : streetName;
        this.registerForm.address = fullStreetAddress;
        this.registerForm.city = city;
        this.registerForm.state = state;
        this.registerForm.zip = zipCode || '';
        this.registerForm.fullAddress = place.formatted_address || addressInput.value;
      });
    }
  },
  
  mounted() {
    // Set initial email values
    this.loginForm.email = this.userEmail;
    this.registerForm.email = this.userEmail;

    // Initialize Google Places Autocomplete
    this.initializeAddressAutocomplete();

    // Load Cloudflare Turnstile
    this.loadTurnstile();
  }
};
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-from {
  transform: translateY(-20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.auth-fields-wrapper {
  background-color: #FDF2F8;
  border: 1px solid #FBB6CE;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 1rem;
}

.form-input:focus {
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
  outline: none;
}
</style>
